import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, CalendarDays, Search as SearchIcon, Mail, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PageHeader, Panel, EmptyState, Markdown } from "@/components/common";
import { api, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

const statusMeta = {
  sent: { label: "Emailed", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  sending: { label: "Sending", icon: Clock, cls: "bg-amber-100 text-amber-900 border-amber-200" },
  failed: { label: "Failed", icon: AlertCircle, cls: "bg-rose-100 text-rose-900 border-rose-200" },
};

export default function BriefingHistory() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const { data } = await api.get("/briefing/history", { params });
      setItems(data);
      if (data.length && !selected) setSelected(data[0].date);
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [range.from, range.to]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (date) => {
    setSelected(date);
    setDetail(null);
    try {
      const { data } = await api.get("/briefing", { params: { date } });
      setDetail(data.briefing);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { if (selected) openDetail(selected); }, [selected, openDetail]);

  const filtered = (items || []).filter((it) => {
    if (!query) return true;
    const label = fmtDate(it.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" }).toLowerCase();
    return label.includes(query.toLowerCase()) || it.date.includes(query);
  });

  return (
    <div data-testid="briefing-history-page">
      <PageHeader
        title="Briefing history"
        subtitle="Every daily briefing your company has generated — in-app and via email."
        actions={<Button variant="secondary" onClick={() => navigate("/briefing")} data-testid="briefing-history-today-button">Today's briefing</Button>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Panel title="Filter" icon={SearchIcon} className="lg:col-span-4 h-fit" testid="briefing-history-filter">
          <div className="space-y-3">
            <Input placeholder="Search by date (e.g. March 10)" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="briefing-history-search" />
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="briefing-history-from" />
              <Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="briefing-history-to" />
            </div>
            {(range.from || range.to) && (
              <Button variant="ghost" size="sm" onClick={() => setRange({ from: "", to: "" })} data-testid="briefing-history-clear-range">Clear dates</Button>
            )}
          </div>
          <div className="mt-5 space-y-1">
            {items === null ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />) :
              filtered.length === 0 ? (
                <EmptyState icon={Newspaper} title="No briefings yet" text="Generate a briefing on the Daily briefing page to see it here." />
              ) : (
                <ul className="space-y-1" data-testid="briefing-history-list">
                  {filtered.map((it) => {
                    const meta = statusMeta[it.email_status] || null;
                    const Icon = meta?.icon;
                    return (
                      <li key={it.date}>
                        <button onClick={() => setSelected(it.date)} data-testid="briefing-history-item"
                          className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${selected === it.date ? "bg-muted border-border" : "border-transparent hover:bg-muted/60"}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium">{fmtDate(it.date, { weekday: "short", month: "short", day: "numeric" })}</span>
                            {meta && (
                              <Badge variant="outline" className={`${meta.cls} font-normal flex items-center gap-1`}>
                                <Icon className="h-3 w-3" /> {meta.label}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">Generated {it.generated_at ? fmtDateTime(it.generated_at) : "-"}</p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )
            }
          </div>
        </Panel>

        <section className="lg:col-span-8 bg-card border border-border rounded-xl p-5 sm:p-7" data-testid="briefing-history-detail">
          {!selected ? (
            <EmptyState icon={CalendarDays} title="Pick a day" text="Choose a date from the list to open that briefing." />
          ) : detail === null ? (
            <div className="space-y-3"><Skeleton className="h-6 w-1/2" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-24 w-full" /></div>
          ) : !detail ? (
            <EmptyState icon={Newspaper} title="No briefing stored for that day" text="If a cron run was skipped (e.g. all modules empty), no briefing is recorded." />
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Daily briefing</p>
                  <h2 className="text-xl font-semibold" data-testid="briefing-history-detail-title">{fmtDate(detail.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</h2>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {[["Meetings", detail.kpis?.meetings_today], ["Overdue", detail.kpis?.overdue_tasks], ["Pending leave", detail.kpis?.pending_leave], ["Headcount", detail.kpis?.headcount]].map(([l, v]) => (
                    <span key={l} className="text-xs bg-muted rounded-full px-3 py-1"><span className="text-muted-foreground">{l}</span> <span className="font-semibold tabular-nums">{v ?? 0}</span></span>
                  ))}
                </div>
              </div>
              <Markdown>{detail.content}</Markdown>
              <div className="mt-6 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>Generated {fmtDateTime(detail.generated_at)}{detail.generated_by ? ` for ${detail.generated_by}` : ""}</span>
                {detail.email && (
                  <span className="flex items-center gap-1" data-testid="briefing-history-email-status"><Mail className="h-3.5 w-3.5" /> Emailed to {detail.email.recipients} on {fmtDateTime(detail.email.sent_at)}</span>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
